import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { returnSign } from 'helpers/helpers'
import { httpOk, GET } from 'helpers/api'

const cardStyle = {
	border: '1px solid var(--dark-border, #edf0f4)',
	borderRadius: '12px',
	boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)',
}

const mutedStyle = {
	color: 'var(--dark-text-muted, #8791a1)',
	fontSize: '12px',
}

const countCellStyle = {
	fontWeight: 600,
	color: 'var(--dark-text-strong, #111827)',
	whiteSpace: 'nowrap',
}

const thStyle = {
	border: 'none',
	boxShadow: 'none',
	background: 'var(--dark-surface, #fff)',
	fontSize: '13px',
}

// Чем выше процент, тем «зеленее» бейдж — чтобы глазом ловить, где партнёр зарабатывает больше
function getPercentBadgeStyle(percentage) {
	if (Number(percentage) >= 50) {
		return { background: 'var(--dark-tint-success, #ecfdf3)', color: 'var(--dark-fg-success, #1f9d57)' }
	}
	if (Number(percentage) >= 30) {
		return { background: 'var(--dark-tint-warning, #fff7df)', color: 'var(--dark-fg-warning, #b7791f)' }
	}
	return { background: 'var(--dark-tint-danger, #fdecec)', color: 'var(--dark-fg-danger, #d23434)' }
}

function PartnerTreeRows({ data, percentages, level = 0 }) {
	return (
		<>
			{data?.map((item) => (
				<Fragment key={item.id}>
					<tr>
						<td style={{ paddingLeft: `${level * 18 + 12}px` }}>
							<div style={{ fontWeight: 600, color: 'var(--dark-text-strong, #111827)' }}>
								{level > 0 && <span style={{ color: '#cbd5e1', marginRight: '6px' }}>└</span>}
								#{item.id} {item.first_name || item.login}
							</div>
							<div style={mutedStyle}>{item.login}</div>
						</td>
						{percentages.map((percentage) => (
							<td key={percentage} className="text-center" style={countCellStyle}>
								{item.counts?.[percentage]
									? item.counts[percentage]
									: <span style={{ color: '#cbd5e1' }}>—</span>
								}
							</td>
						))}
						<td className="text-center" style={{ ...countCellStyle, fontWeight: 700 }}>
							{item.total || 0}
						</td>
					</tr>
					{!!item.children?.length &&
						<PartnerTreeRows
							data={item.children}
							percentages={percentages}
							level={level + 1}
						/>
					}
				</Fragment>
			))}
		</>
	)
}

function Index() {
	const { t } = useTranslation()

	const statuses = [
		{ 'id': 1, 'name': t('active') },
		{ 'id': -1, 'name': t('disconnected') },
		{ 'id': -3, 'name': t('deleted') },
	]

	const [selectedStatuses, setSelectedStatuses] = useState([statuses[0]])
	const [percentages, setPercentages] = useState([])
	const [tree, setTree] = useState([])
	const [totalPoses, setTotalPoses] = useState(0)
	const [loaded, setLoaded] = useState(false)

	async function getData(nextStatuses = selectedStatuses) {
		let url = '/partner/pos-percentages'

		if (nextStatuses?.length) {
			const statusIds = nextStatuses.map((item) => item.id).join(',')
			url += returnSign(url) + `status_ids=${statusIds}`
		}

		const response = await GET(url, {}, { loader: true })
		if (httpOk(response)) {
			setPercentages(response.data?.percentages || [])
			setTree(response.data?.tree || [])
			setTotalPoses(response.data?.total_poses || 0)
			setLoaded(true)
		}
	}

	useEffect(() => {
		getData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div className="pb-3">
			<div className="page-header">
				<h4>{t('partner_pos_percentages')}</h4>
			</div>

			<div className="card mb-3" style={cardStyle}>
				<div className="card-body">
					<div className="row g-3 align-items-end">
						<div className="col-md-6">
							<label className="form-label">{t('status')}</label>
							<Select
								isMulti
								isClearable
								options={statuses}
								value={selectedStatuses}
								onChange={(options) => setSelectedStatuses(options ? options : [])}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="col-md-3">
							<button type="button" className="btn btn-primary w-100" onClick={() => getData()}>
								{t('apply_action')}
							</button>
						</div>
					</div>
					<div className="text-muted small mt-2" style={mutedStyle}>
						{t('partner_pos_percentages_hint')}
					</div>
				</div>
			</div>

			<div className="card" style={cardStyle}>
				<div className="card-body">
					<div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
						<div>
							<div style={{ fontWeight: 700, color: 'var(--dark-text-strong, #111827)' }}>{t('partner_pos_percentages')}</div>
							<div style={mutedStyle}>
								{selectedStatuses?.length
									? selectedStatuses.map((item) => item.name).join(', ')
									: t('status')
								}
							</div>
						</div>
						{loaded && tree.length > 0 &&
							<div style={mutedStyle}>
								{t('poses')}:{' '}
								<span style={{ fontWeight: 700, color: 'var(--dark-text-strong, #111827)' }}>{totalPoses}</span>
							</div>
						}
					</div>

					<div className="table-responsive">
						<table className="table table-hover align-middle mb-0" style={{ fontSize: '13px' }}>
							<thead>
								<tr style={{ border: 'none' }}>
									<th className="py-3 text-muted fw-semibold" style={thStyle}>{t('partner')}</th>
									{percentages.map((percentage) => (
										<th key={percentage} className="py-3 text-muted fw-semibold text-center" style={thStyle}>
											<span
												style={{
													...getPercentBadgeStyle(percentage),
													borderRadius: '8px',
													padding: '3px 10px',
													fontWeight: 600,
													fontSize: '12px',
													display: 'inline-block',
													minWidth: '48px',
												}}
											>
												{percentage}%
											</span>
										</th>
									))}
									<th className="py-3 text-muted fw-semibold text-center" style={thStyle}>{t('total')}</th>
								</tr>
							</thead>
							<tbody>
								{loaded && tree.length === 0 &&
									<tr>
										<td colSpan={percentages.length + 2} className="text-center text-muted py-4">
											{t('no_data')}
										</td>
									</tr>
								}
								<PartnerTreeRows data={tree} percentages={percentages} />
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	)
}

export default Index
